import { LogIn, LogOut, Monitor, Terminal, MonitorPlay, Shield, User } from 'lucide-react'
import { type AuditLogEvent } from '../lib/api'

// Action type configuration
const ACTION_CONFIG: Record<string, { icon: React.ReactNode; color: string; bgColor: string; label: string }> = {
  login: { icon: <LogIn className="w-3 h-3" />, color: 'text-green-500', bgColor: 'bg-green-500/10', label: 'Login' },
  logout: { icon: <LogOut className="w-3 h-3" />, color: 'text-gray-400', bgColor: 'bg-gray-500/10', label: 'Logout' },
  workspace_code: { icon: <Monitor className="w-3 h-3" />, color: 'text-blue-500', bgColor: 'bg-blue-500/10', label: 'Code Editor' },
  workspace_terminal: { icon: <Terminal className="w-3 h-3" />, color: 'text-purple-500', bgColor: 'bg-purple-500/10', label: 'Terminal' },
  workspace_desktop: { icon: <MonitorPlay className="w-3 h-3" />, color: 'text-amber-500', bgColor: 'bg-amber-500/10', label: 'Desktop' },
  admin_provision: { icon: <Shield className="w-3 h-3" />, color: 'text-cyan-500', bgColor: 'bg-cyan-500/10', label: 'Workspace Provisioned' },
  admin_impersonate: { icon: <User className="w-3 h-3" />, color: 'text-orange-500', bgColor: 'bg-orange-500/10', label: 'Impersonated' },
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

interface ActivityTimelineProps {
  events: AuditLogEvent[]
  loading?: boolean
  showUser?: boolean
}

export default function ActivityTimeline({ events, loading, showUser = false }: ActivityTimelineProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (events.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No activity recorded yet
      </div>
    )
  }

  function getActionDisplay(action: string) {
    return ACTION_CONFIG[action] || {
      icon: <User className="w-3 h-3" />,
      color: 'text-muted-foreground',
      bgColor: 'bg-muted',
      label: action.replace(/_/g, ' '),
    }
  }

  return (
    <div className="space-y-3 max-h-96 overflow-auto">
      {events.map((event) => {
        const actionDisplay = getActionDisplay(event.action)
        return (
          <div
            key={event.id}
            className={`flex items-start gap-3 p-3 rounded-lg border border-border ${actionDisplay.bgColor}`}
          >
            {/* Icon */}
            <div className={`mt-0.5 ${actionDisplay.color}`}>
              {actionDisplay.icon}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <span className={`text-sm font-medium ${actionDisplay.color}`}>
                  {actionDisplay.label}
                </span>
                <span className="text-xs text-muted-foreground">
                  {formatRelativeTime(event.created_at)}
                </span>
              </div>

              {showUser && (
                <div className="text-sm text-muted-foreground truncate">
                  {event.user_name || event.email.split('@')[0]}
                </div>
              )}

              {event.resource_id && (
                <div className="text-xs text-muted-foreground mt-1">
                  <code className="px-1 py-0.5 rounded bg-secondary">{event.resource_id}</code>
                </div>
              )}

              {event.ip_address && (
                <div className="text-xs text-muted-foreground mt-1">
                  IP: {event.ip_address}
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
